import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUpload, FaSpinner, FaTrash, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { getImageUrl } from '../utils/imageUtils';
import {
  createCustomProduct,
  updateCustomProduct,
  uploadProductImage,
  fetchProductDetails,
} from '../services/apiService';

const EMPTY_FORM = {
  pharmacode: '',
  description: '',
  longDescription: '',
  brand: '',
  gtin: '',
  supplyCategory: 'E',
  quantity: '',
  price: '',
  images: [],
  substances: [],
  companies: [],
  genericGroupCodes: [],
};

// Resolve a price from a raw article across the same fields the backend uses.
const priceFromRawArticle = (a) => {
  if (!a) return '';
  if (a.basePrice != null) return a.basePrice;
  if (a.publicPrice?.price != null) return a.publicPrice.price;
  const ppub = Array.isArray(a.prices)
    ? a.prices.find((p) => (p.type?.id || p.type) === 'PPUB' && p.price != null)
    : null;
  return ppub?.price ?? '';
};

const toStringList = (arr) =>
  Array.isArray(arr)
    ? arr.map((x) => (typeof x === 'string' ? x : x?.name || x?.description || '')).filter(Boolean)
    : [];

/**
 * Create / edit modal for manual ("from scratch") products. One component for
 * both flows: `mode` is 'create' or 'edit'. In edit mode it fetches the full
 * product so every field (incl. advanced ones not present in the list view)
 * pre-fills correctly. Pharmacode is read-only when editing — it's immutable.
 */
const ProductFormModal = ({ isOpen, mode = 'create', product = null, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileInputRef = useRef(null);

  const isEdit = mode === 'edit';

  // Reset / pre-fill whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSubmitError('');
    setShowAdvanced(false);

    if (!isEdit) {
      setForm(EMPTY_FORM);
      return;
    }

    // Edit: pull the full product so advanced fields pre-fill too.
    const code = product?.pharmacode ?? product?.articles?.[0]?.pharmacode;
    if (code == null) return;

    setIsLoading(true);
    fetchProductDetails(code)
      .then((res) => {
        const p = res?.data?.product;
        const article = p?.articles?.[0] || {};
        const raw = article.rawArticle || article;
        setForm({
          pharmacode: String(p?.pharmacode ?? code),
          description: raw.description?.description || article.description?.description || '',
          longDescription:
            raw.description?.longDescription || article.description?.longDescription || article.longDescription || '',
          brand: raw.brand || '',
          gtin: raw.gtin || '',
          supplyCategory: raw.swissmedicSupplyCategoryCode === 'D' ? 'D' : 'E',
          quantity: raw.quantity ?? '',
          price: priceFromRawArticle(raw),
          images: Array.isArray(raw.images?.F) ? raw.images.F : (Array.isArray(article.images?.F) ? article.images.F : []),
          substances: toStringList(raw.substances),
          companies: toStringList(raw.companies),
          genericGroupCodes: toStringList(raw.genericGroupCodes),
        });
      })
      .catch(() => setSubmitError(t('products.productForm.errors.generic')))
      .finally(() => setIsLoading(false));
  }, [isOpen, isEdit, product, t]);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-selecting same file
    if (!file) return;
    try {
      setIsUploading(true);
      setSubmitError('');
      const res = await uploadProductImage(file);
      const pathStr = res?.data?.path;
      if (pathStr) setForm((f) => ({ ...f, images: [...f.images, pathStr] }));
      else setSubmitError(t('products.productForm.errors.imageUpload'));
    } catch (err) {
      setSubmitError(t('products.productForm.errors.imageUpload'));
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = (idx) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const validate = () => {
    const next = {};
    const code = Number(form.pharmacode);
    if (!isEdit && (!Number.isInteger(code) || code <= 0)) {
      next.pharmacode = t('products.productForm.errors.pharmacodeRequired');
    }
    if (!String(form.description).trim()) {
      next.description = t('products.productForm.errors.descriptionRequired');
    }
    const price = Number(form.price);
    if (!Number.isFinite(price) || price <= 0) {
      next.price = t('products.productForm.errors.priceRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      pharmacode: Number(form.pharmacode),
      description: String(form.description).trim(),
      longDescription: String(form.longDescription || '').trim(),
      brand: String(form.brand || '').trim(),
      gtin: String(form.gtin || '').trim(),
      supplyCategory: form.supplyCategory === 'D' ? 'D' : 'E',
      quantity: form.quantity === '' || form.quantity == null ? null : Number(form.quantity),
      price: Number(form.price),
      images: form.images,
      substances: form.substances,
      companies: form.companies,
      genericGroupCodes: form.genericGroupCodes,
    };

    try {
      setIsSaving(true);
      setSubmitError('');
      const res = isEdit
        ? await updateCustomProduct(form.pharmacode, payload)
        : await createCustomProduct(payload);
      if (onSaved) onSaved(res);
      onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setErrors((e) => ({ ...e, pharmacode: t('products.productForm.errors.pharmacodeExists') }));
      } else {
        setSubmitError(err.response?.data?.message || t('products.productForm.errors.generic'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const errClass = 'mt-1 text-xs text-red-600';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 10 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEdit ? t('products.productForm.editTitle') : t('products.productForm.createTitle')}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {isEdit ? t('products.productForm.editSubtitle') : t('products.productForm.createSubtitle')}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close">
              <FaTimes className="text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 overflow-y-auto space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <FaSpinner className="animate-spin mr-2" /> ...
              </div>
            ) : (
              <>
                {submitError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                {/* Basic information */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    {t('products.productForm.sections.basic')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.pharmacode')} *</label>
                      <input
                        type="number"
                        className={`${inputClass} ${isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        value={form.pharmacode}
                        onChange={(e) => setField('pharmacode', e.target.value)}
                        disabled={isEdit}
                      />
                      {errors.pharmacode ? (
                        <div className={errClass}>{errors.pharmacode}</div>
                      ) : (
                        <div className="mt-1 text-xs text-gray-400">{t('products.productForm.fields.pharmacodeHelp')}</div>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.brand')}</label>
                      <input className={inputClass} value={form.brand} onChange={(e) => setField('brand', e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>{t('products.productForm.fields.description')} *</label>
                      <input
                        className={inputClass}
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                      />
                      {errors.description && <div className={errClass}>{errors.description}</div>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>{t('products.productForm.fields.longDescription')}</label>
                      <textarea
                        rows={2}
                        className={inputClass}
                        value={form.longDescription}
                        onChange={(e) => setField('longDescription', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.gtin')}</label>
                      <input className={inputClass} value={form.gtin} onChange={(e) => setField('gtin', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.supplyCategory')}</label>
                      <select
                        className={inputClass}
                        value={form.supplyCategory}
                        onChange={(e) => setField('supplyCategory', e.target.value)}
                      >
                        <option value="E">{t('products.productForm.supplyOptions.E')}</option>
                        <option value="D">{t('products.productForm.supplyOptions.D')}</option>
                      </select>
                    </div>
                  </div>
                </section>

                {/* Pricing */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    {t('products.productForm.sections.pricing')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.price')} *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputClass}
                        value={form.price}
                        onChange={(e) => setField('price', e.target.value)}
                      />
                      {errors.price && <div className={errClass}>{errors.price}</div>}
                    </div>
                    <div>
                      <label className={labelClass}>{t('products.productForm.fields.quantity')}</label>
                      <input
                        type="number"
                        min="0"
                        className={inputClass}
                        value={form.quantity}
                        onChange={(e) => setField('quantity', e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                {/* Image */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    {t('products.productForm.fields.image')}
                  </h3>
                  <div className="flex flex-wrap items-center gap-3">
                    {form.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                        <img src={getImageUrl(img)} alt="" className="w-full h-full object-contain" />
                        <button
                          onClick={() => removeImage(idx)}
                          className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-1 text-red-500 hover:text-red-700 shadow"
                          aria-label={t('products.productForm.image.remove')}
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
                    >
                      {isUploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                      <span className="text-[10px] mt-1">
                        {isUploading ? t('products.productForm.image.uploading') : t('products.productForm.image.upload')}
                      </span>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                  </div>
                  <div className="mt-2 text-xs text-gray-400">{t('products.productForm.image.hint')}</div>
                </section>

                {/* Advanced */}
                <section>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((s) => !s)}
                    className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                  >
                    {showAdvanced ? <FaChevronUp /> : <FaChevronDown />}
                    {showAdvanced ? t('products.productForm.advanced.toggleHide') : t('products.productForm.advanced.toggleShow')}
                  </button>
                  {showAdvanced && (
                    <div className="mt-4 space-y-4">
                      <TagInput
                        label={t('products.productForm.advanced.substances')}
                        values={form.substances}
                        onChange={(v) => setField('substances', v)}
                        addLabel={t('products.productForm.advanced.addItem')}
                        hint={t('products.productForm.advanced.listHint')}
                        inputClass={inputClass}
                        labelClass={labelClass}
                      />
                      <TagInput
                        label={t('products.productForm.advanced.companies')}
                        values={form.companies}
                        onChange={(v) => setField('companies', v)}
                        addLabel={t('products.productForm.advanced.addItem')}
                        hint={t('products.productForm.advanced.listHint')}
                        inputClass={inputClass}
                        labelClass={labelClass}
                      />
                      <TagInput
                        label={t('products.productForm.advanced.genericGroupCodes')}
                        values={form.genericGroupCodes}
                        onChange={(v) => setField('genericGroupCodes', v)}
                        addLabel={t('products.productForm.advanced.addItem')}
                        hint={t('products.productForm.advanced.listHint')}
                        inputClass={inputClass}
                        labelClass={labelClass}
                      />
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
            >
              {t('products.productForm.actions.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || isLoading || isUploading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm hover:from-indigo-600 hover:to-purple-700 disabled:opacity-60"
            >
              {isSaving && <FaSpinner className="animate-spin" />}
              {isEdit
                ? isSaving
                  ? t('products.productForm.actions.saving')
                  : t('products.productForm.actions.save')
                : isSaving
                ? t('products.productForm.actions.creating')
                : t('products.productForm.actions.create')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Small tag-list input for the optional advanced string lists.
const TagInput = ({ label, values, onChange, addLabel, hint, inputClass, labelClass }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex gap-2">
        <input
          className={inputClass}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-3 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm"
        >
          <FaPlus className="text-xs" /> {addLabel}
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.map((v, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-full">
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((_, i) => i !== idx))}
                className="hover:text-indigo-900"
              >
                <FaTimes className="text-[10px]" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-1 text-xs text-gray-400">{hint}</div>
    </div>
  );
};

export default ProductFormModal;
